<!-- src/components/pettyCash/PettyCashTable.vue -->
<template>
  <div class="petty-cash-table-root">
    <p v-if="rows.length === 0" class="grey-text">
      No petty cash lines recorded for this week yet.
    </p>

    <div v-else class="table-wrap">
      <table class="highlight responsive-table petty-cash-table">
        <thead>
          <tr>
            <th>Description</th>
            <th>Category</th>
            <th class="right-align">Amount</th>
            <th>Expense date</th>
            <th>Entered</th>
            <th class="center-align">Is claimed back</th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="e in rows"
            :key="e._rowKey"
            :class="{ 'grey lighten-4': isClaimedBack(e) }"
          >
            <td>
              {{ e.description }}

              <div v-if="e.isLate" class="grey-text text-darken-1 petty-cash-note">
                Late receipt (entered this week)
              </div>

              <div v-if="e.isOutstanding" class="grey-text text-darken-1 petty-cash-note">
                Previous unclaimed receipt
              </div>
            </td>

            <td>
              {{ e.category }}
            </td>

            <td class="right-align">£{{ money2dp(e.amount) }}</td>

            <td>
              {{ formatDateOnly(e.expense_date) }}
            </td>

            <td>
              {{ formatCreated(e.created_at) }}
            </td>

            <!-- Permanent claimed checkbox -->
            <td class="center-align">
              <label>
                <input
                  type="checkbox"
                  :checked="isClaimedBack(e)"
                  :disabled="isClaimedBack(e)"
                  @change="onClaimToggle(e)"
                />

                <span></span>
              </label>
            </td>
          </tr>
        </tbody>

        <tfoot>
          <tr>
            <th colspan="2" class="right-align">Unclaimed total</th>

            <th class="right-align">£{{ money2dp(unclaimedTotal) }}</th>

            <th colspan="3"></th>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";

import { moneyGBP } from "@/scripts/sharedHelpers/formatter";

const props = defineProps({
  rows: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["toggle-claimed"]);

/* ===================== Money ===================== */

function money2dp(v) {
  return moneyGBP(v, 2).replace("£", "").trim();
}

/* ===================== Dates ===================== */

function parseISODateOnly(value) {
  const raw = String(value || "").trim();

  if (!raw) {
    return null;
  }

  const isoDateOnly = raw.slice(0, 10);

  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDateOnly)) {
    return null;
  }

  const [year, month, day] = isoDateOnly.split("-").map(Number);

  if (!year || !month || !day) {
    return null;
  }

  // Local date prevents UTC timezone shifting the displayed day.
  return new Date(year, month - 1, day);
}

function formatDateObject(date) {
  if (!date || Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

function formatDateOnly(value) {
  const date = parseISODateOnly(value);

  return formatDateObject(date);
}

function formatCreated(ts) {
  if (!ts) {
    return "";
  }

  let date = null;

  if (ts?.toDate) {
    date = ts.toDate();
  } else if (ts?.seconds) {
    date = new Date(ts.seconds * 1000);
  } else if (typeof ts === "string") {
    if (/^\d{4}-\d{2}-\d{2}/.test(ts)) {
      date = parseISODateOnly(ts);
    } else {
      const parsed = Date.parse(ts);

      if (Number.isFinite(parsed)) {
        date = new Date(parsed);
      }
    }
  }

  return formatDateObject(date);
}

/* ===================== Claimed state ===================== */

function toBool(v) {
  if (v === true) {
    return true;
  }

  if (v === false) {
    return false;
  }

  if (typeof v === "string") {
    return v.toLowerCase() === "true";
  }

  return Boolean(v);
}

function isClaimedBack(row) {
  return toBool(row?.is_claimed_back) || toBool(row?.is_calimed_back);
}

const unclaimedTotal = computed(() =>
  (props.rows || []).reduce((sum, r) => {
    if (isClaimedBack(r)) {
      return sum;
    }

    return sum + Number(r?.amount || 0);
  }, 0)
);

function onClaimToggle(row) {
  if (isClaimedBack(row)) {
    return;
  }

  emit("toggle-claimed", {
    ...row,

    is_claimed_back: true,

    is_calimed_back: true,
  });
}
</script>

<style scoped>
.petty-cash-table-root {
  width: 100%;
}

.petty-cash-note {
  font-size: 12px;
}

@media (max-width: 600px) {
  .petty-cash-table {
    min-width: 760px;
  }

  .petty-cash-table td:first-child {
    min-width: 180px;
  }

  .petty-cash-table label {
    display: inline-flex;
    justify-content: center;
    min-width: 34px;
  }
}
</style>
